import * as React from "react"
import { type IntervalOrigin } from "@digits-graphql/frontend/graphql-bearer"
import { SvgChevronLeft } from "@digits-shared/components/SVGIcons/line/ChevronLeft.svg"
import { SvgChevronRight } from "@digits-shared/components/SVGIcons/line/ChevronRight.svg"
import { type DigitsButtonProps } from "@digits-shared/DesignSystem/Button"
import { DigitsLinkButton } from "@digits-shared/DesignSystem/LinkButton"
import { DigitsTooltip } from "@digits-shared/DesignSystem/Tooltip"
import dateTimeHelper from "@digits-shared/helpers/dateTimeHelper"
import useRouter from "@digits-shared/hooks/useRouter"
import styled, { type CSSProp } from "styled-components"
import { useFrontendPathGenerator } from "src/frontend/hooks/useFrontendPathGenerator"
import routes from "src/frontend/routes"
import {
  type PeriodSelectButtonProps,
  PeriodSelectorButton,
} from "src/shared/components/PeriodSelector/PeriodSelectorButton"
import { usePeriodSelectorActiveOrigin } from "src/shared/components/PeriodSelector/usePeriodSelectorActiveOrigin"

/*
  STYLES
*/

const PeriodNav = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
`

/*
  INTERFACES
*/

interface SelectorNav extends PeriodSelectButtonProps {
  navCSS?: CSSProp
}

interface NavProps extends DigitsButtonProps {
  intervalOrigin: IntervalOrigin
  tooltip: string
  disabled?: boolean
  side: "left" | "right"
}

/*
  COMPONENTS
*/

export const PeriodSelectorNav: React.FC<SelectorNav> = ({ navCSS, ...props }) => (
  <PeriodNav css={navCSS}>
    <NavLeft $variant={props.$variant} size={props.size} />
    <PeriodSelectorButton {...props} />
    <NavRight $variant={props.$variant} size={props.size} />
  </PeriodNav>
)

const NavLeft: React.FC<DigitsButtonProps> = ({ $variant, size }) => {
  const [intervalOrigin] = usePeriodSelectorActiveOrigin()
  const count = intervalOrigin.intervalCount ?? 1
  const prevIntervalOrigin = dateTimeHelper.addIntervalToOrigin(intervalOrigin, -count)
  prevIntervalOrigin.intervalCount = undefined
  const prevTooltip = dateTimeHelper.displayNameFromIntervalOrigin(prevIntervalOrigin)

  return (
    <NavButton
      tooltip={prevTooltip}
      intervalOrigin={prevIntervalOrigin}
      $variant={$variant}
      size={size}
      side="left"
    >
      <SvgChevronLeft />
    </NavButton>
  )
}

const NavRight: React.FC<DigitsButtonProps> = ({ $variant, size }) => {
  const [intervalOrigin] = usePeriodSelectorActiveOrigin()
  const count = intervalOrigin.intervalCount ?? 1
  const todayIntervalOrigin = dateTimeHelper.todayIntervalOrigin(intervalOrigin.interval, count)

  const disabled = dateTimeHelper.areIntervalOriginsEqual(todayIntervalOrigin, intervalOrigin, true)

  let nextIntervalOrigin = dateTimeHelper.addIntervalToOrigin(intervalOrigin, count)
  if (dateTimeHelper.occurredAfterIntervalOrigin(nextIntervalOrigin, todayIntervalOrigin)) {
    nextIntervalOrigin = todayIntervalOrigin
  }
  nextIntervalOrigin.intervalCount = undefined
  const nextTooltip = dateTimeHelper.displayNameFromIntervalOrigin(nextIntervalOrigin)

  return (
    <NavButton
      tooltip={nextTooltip}
      intervalOrigin={nextIntervalOrigin}
      disabled={disabled}
      $variant={$variant}
      size={size}
      side="right"
    >
      <SvgChevronRight />
    </NavButton>
  )
}

const StyledButton = styled(DigitsLinkButton)`
  &.left {
    border-radius: 16px 0 0 16px;
    padding: 0 4px 0 10px;
  }
  &.right {
    border-radius: 0 16px 16px 0;
    padding: 0 7px 0 7px;
  }
`

const NavButton: React.FC<React.PropsWithChildren<NavProps>> = ({
  children,
  intervalOrigin,
  tooltip,
  disabled,
  $variant = "secondary-white",
  size = "medium",
  side,
}) => {
  const { location } = useRouter()
  const generatePath = useFrontendPathGenerator()
  const path = generatePath(routes[location.name], { ...intervalOrigin }, "includeAllQueryParams")
  return (
    <DigitsTooltip content={tooltip}>
      <StyledButton
        className={side}
        to={path}
        replace={true}
        $variant={$variant}
        size={size}
        disabled={disabled}
      >
        {children}
      </StyledButton>
    </DigitsTooltip>
  )
}
